import { isAxiosError } from "axios";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { THERMOSTAT } from "@volca/ui/components/ui/empty-machine";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { isApiErrorResponse } from "@/core/api/errors";
import { PayBreakdownPaneShell } from "@/payroll/pay-breakdown/pay-breakdown-pane-shell";
import { usePayPlan } from "@/payroll/pay-breakdown/pay-plan-query";
import { RunSnapshotHeader } from "@/payroll/pay-breakdown/run-snapshot-header";
import { PayPlanMarkdown } from "@/payroll/pay-plan-markdown";
import { PAYROLL_EDITOR_PATH } from "@/payroll/settings/routes";
import { PayPlanAccessState } from "./pay-plan-access-state";

interface PayBreakdownPayPlanTabProps {
  businessId: string;
  runId: string;
  /**
   * Whether this user may edit the rules, decided by the server from the
   * feature flag and their authority. The page already holds the answer, so
   * this pane takes it rather than asking again.
   */
  canEditRules?: boolean;
}

export const PayBreakdownPayPlanTab = ({
  businessId,
  runId,
  canEditRules = false,
}: PayBreakdownPayPlanTabProps) => {
  const { data, isLoading, isError, error, refetch, isFetching } = usePayPlan(
    businessId,
    runId,
  );
  const planDocument = data?.plan_document ?? "";
  const hasPlanDocument = planDocument.trim().length > 0;
  const isForbidden =
    (isApiErrorResponse(error) && error.status === 403) ||
    (isAxiosError(error) && error.response?.status === 403);

  if (isLoading)
    return (
      <PayBreakdownPaneShell fillHeight className="min-h-0">
        <Stack
          align="center"
          justify="center"
          className="min-h-0 w-full flex-1"
          role="status"
          aria-label="Loading pay plan"
        >
          <Spinner layout="centered" />
        </Stack>
      </PayBreakdownPaneShell>
    );

  if (isForbidden)
    return (
      <PayBreakdownPaneShell fillHeight className="min-h-0">
        <PayPlanAccessState businessId={businessId} />
      </PayBreakdownPaneShell>
    );

  if (isError || !hasPlanDocument)
    return (
      <PayBreakdownPaneShell fillHeight className="min-h-0">
        {isError ? (
          <ErrorState
            title="Pay plan could not be loaded"
            description="Try loading this pay plan again."
            retrying={isFetching}
          >
            <Button
              variant="outline"
              size="sm"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              Try again
            </Button>
          </ErrorState>
        ) : (
          <MachineEmptyState
            art={THERMOSTAT}
            layout="workspace"
            title="No pay plan available"
            description="This payroll run doesn't have a pay plan document."
            footnote="The saved plan normally appears here so you can check the rules used for this run."
          />
        )}
      </PayBreakdownPaneShell>
    );

  return (
    <PayBreakdownPaneShell fillHeight className="min-h-0">
      <Card className="flex min-h-0 w-full flex-1 flex-col gap-0 overflow-hidden py-0">
        {data && hasPlanDocument && (
          <RunSnapshotHeader
            className="px-4 sm:px-8"
            badges={
              <Badge variant="secondary" size="sm">
                Script v{data.ai_script_version_number}
              </Badge>
            }
            description="The pay plan this period's payroll ran against."
            action={
              canEditRules
                ? { to: PAYROLL_EDITOR_PATH, label: "Edit rules" }
                : undefined
            }
          />
        )}

        <CardContent className="flex min-h-0 flex-1 flex-col overflow-y-auto p-0">
          <Stack align="start" gap={0} className="w-full px-4 py-6 sm:px-8">
            <PayPlanMarkdown document={planDocument} className="w-full" />
          </Stack>
        </CardContent>
      </Card>
    </PayBreakdownPaneShell>
  );
};
